/**
 * POS-PRODUCT-STATS-UX-001B/001D — helpers de vista (dataset agregado, sin query).
 */

export function normalizarTextoBusqueda(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/** Compatibilidad: Functions sin deploy 001B solo envían productosDestacados. */
export function resolverFilasReporteVentas(payload) {
  if (Array.isArray(payload?.productos) && payload.productos.length > 0) {
    return payload.productos;
  }
  if (Array.isArray(payload?.productosDestacados) && payload.productosDestacados.length > 0) {
    return payload.productosDestacados;
  }
  return [];
}

export function resolverFilasReporteGanancias(payload) {
  const raw = Array.isArray(payload?.productos) && payload.productos.length > 0
    ? payload.productos
    : (Array.isArray(payload?.productosMasVendidos) && payload.productosMasVendidos.length > 0
      ? payload.productosMasVendidos
      : (Array.isArray(payload?.topProductosPorGanancia) ? payload.topProductosPorGanancia : []));
  return raw.map((p) => ({
    id: p.id,
    ganancia: p.ganancia,
    margen: p.margen
  }));
}

export function topProductosPorUnidades(rows = [], limite = 10) {
  return ordenarProductos(rows, 'cantidad', 'desc').slice(0, limite);
}

export function sugerirProductosCatalogo(productos = [], termino = '', limite = 10) {
  const q = normalizarTextoBusqueda(termino);
  if (q.length < 2) return [];
  const scored = [];
  (productos || []).forEach((p) => {
    if (!p || p.id == null) return;
    const nombre = normalizarTextoBusqueda(p.nombre);
    const codigo = normalizarTextoBusqueda(p.codigo);
    if (!nombre.includes(q) && !codigo.includes(q)) return;
    const exactCodigo = codigo === q;
    const starts = nombre.startsWith(q) || codigo.startsWith(q);
    scored.push({
      id: p.id,
      nombre: p.nombre || 'Producto',
      codigo: p.codigo || '',
      rank: exactCodigo ? 0 : (starts ? 1 : 2)
    });
  });
  scored.sort((a, b) => a.rank - b.rank || String(a.nombre).localeCompare(String(b.nombre)));
  const seen = new Set();
  const out = [];
  scored.forEach((p) => {
    const key = String(p.id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(p);
  });
  return out.slice(0, limite);
}

export function mergeProductosVentasGanancias(ventasRows = [], gananciasRows = []) {
  const ganById = new Map();
  (gananciasRows || []).forEach((row) => {
    if (row && row.id != null) ganById.set(String(row.id), row);
  });
  return (ventasRows || []).map((row) => {
    const g = ganById.get(String(row.id));
    return {
      id: row.id,
      nombre: row.nombre || 'Producto',
      codigo: row.codigo || '',
      cantidad: Number(row.cantidad) || 0,
      total: Number(row.total) || 0,
      porcentaje: Number(row.porcentaje) || 0,
      ganancia: g ? Number(g.ganancia) || 0 : null,
      margen: g ? Number(g.margen) || 0 : null
    };
  });
}

export function resumenProductos(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  const unidades = list.reduce((acc, r) => acc + (Number(r.cantidad) || 0), 0);
  const facturacion = list.reduce((acc, r) => acc + (Number(r.total) || 0), 0);
  const ganancia = list.every((r) => r.ganancia == null)
    ? null
    : list.reduce((acc, r) => acc + (Number(r.ganancia) || 0), 0);
  let masVendido = null;
  list.forEach((r) => {
    if (!masVendido || (Number(r.cantidad) || 0) > (Number(masVendido.cantidad) || 0)) {
      masVendido = r;
    }
  });
  const margen = facturacion > 0 && ganancia != null ? (ganancia / facturacion) * 100 : null;
  return {
    unidades,
    facturacion,
    productosVendidos: list.length,
    masVendido,
    ganancia,
    margen
  };
}

export function filtrarProductos(rows = [], termino = '') {
  const q = String(termino || '').trim().toLowerCase();
  if (!q) return Array.isArray(rows) ? rows : [];
  return (rows || []).filter((r) => {
    const nombre = String(r.nombre || '').toLowerCase();
    const codigo = String(r.codigo || '').toLowerCase();
    return nombre.includes(q) || codigo.includes(q);
  });
}

export function ordenarProductos(rows = [], campo = 'cantidad', dir = 'desc') {
  const list = [...(rows || [])];
  const key = campo === 'total' || campo === 'ventas' ? 'total'
    : campo === 'unidades' || campo === 'cantidad' ? 'cantidad'
    : campo === 'ganancia' ? 'ganancia'
    : campo === 'margen' ? 'margen'
    : campo;
  const mul = dir === 'asc' ? 1 : -1;
  list.sort((a, b) => {
    const av = Number(a[key]) || 0;
    const bv = Number(b[key]) || 0;
    if (av === bv) return String(a.nombre || '').localeCompare(String(b.nombre || ''));
    return (av - bv) * mul;
  });
  return list;
}

export function csvProductos(rows = [], { incluirFinanzas = false } = {}) {
  const headers = incluirFinanzas
    ? ['Producto', 'Codigo', 'Unidades', 'Ventas', 'Participacion %', 'Ganancia', 'Margen %']
    : ['Producto', 'Codigo', 'Unidades', 'Ventas', 'Participacion %'];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const lines = [headers.join(',')];
  (rows || []).forEach((r) => {
    const base = [
      escape(r.nombre),
      escape(r.codigo),
      Number(r.cantidad) || 0,
      Number(r.total) || 0,
      (Number(r.porcentaje) || 0).toFixed(2)
    ];
    if (incluirFinanzas) {
      base.push(Number(r.ganancia) || 0);
      base.push((Number(r.margen) || 0).toFixed(2));
    }
    lines.push(base.join(','));
  });
  return lines.join('\n');
}

export function descargarCsv(contenido, filename) {
  const blob = new Blob([`\uFEFF${contenido}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
